import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { returnProviderFunds } from '@/api/wallet';
import { useAuthStore } from '@/store/auth';

/**
 * Pages that should always start with the funds back in the main wallet —
 * anywhere the player is about to look at, spend or move their balance.
 *
 * Matched exactly, so `/game` fires but `/game/list/...` does not.
 */
const RETURN_ON_ENTER = [
  '/',
  '/game',
  '/deposit',
  '/withdraw',
  '/activity',
  '/mine',
  '/wallet/record',
  '/mine/game-data',
];

/** Inside a vendor venue the funds belong in the provider's wallet. */
const isVenue = (pathname: string) => pathname.startsWith('/game/play/');

/**
 * Whether arriving at `pathname` from `prev` should sweep the wallet.
 *
 * Kept separate from the effect so the routing rules read as one rule;
 * de-duplication of repeat fires stays in the hook.
 */
function shouldReturnFunds(prev: string | null, pathname: string, isAuthed: boolean): boolean {
  // The call is authenticated; there is nothing to return when signed out.
  if (!isAuthed) return false;
  // Never while the player is still in the venue — that would pull the funds
  // out from under a live session.
  if (isVenue(pathname)) return false;
  const leftVenue = prev !== null && isVenue(prev);
  return leftVenue || RETURN_ON_ENTER.includes(pathname);
}

/**
 * Returns provider-wallet funds to the main wallet on navigation.
 *
 * Fires when the player leaves a game venue, and when they land on any of the
 * balance-facing pages above — including on first load, so a deep link into
 * /mine settles the wallet too.
 *
 * Mounted once at the router root rather than called from each page: the rule
 * is about navigation, so keeping it in one place means a new page can't
 * quietly miss it, and there is a single list to change.
 */
export function useReturnProviderFunds() {
  const { pathname, search } = useLocation();
  const isAuthed = useAuthStore((s) => s.isAuthed());

  const prevPathRef = useRef<string | null>(null);
  const lastFiredRef = useRef<string | null>(null);

  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = pathname;

    if (!shouldReturnFunds(prev, pathname, isAuthed)) return;

    // Query string included so /mine/game-data?tab=history counts as its own
    // arrival, per the requirement.
    const key = `${pathname}${search}`;
    // Re-renders and StrictMode's double-invoke must not double-post; a real
    // second arrival always changes this key first.
    if (key === lastFiredRef.current) return;

    lastFiredRef.current = key;
    // Best-effort: a failed sweep must not surface an error on a page the
    // player opened for something else. Concurrent triggers coalesce inside
    // returnProviderFunds.
    returnProviderFunds().catch(() => {});
  }, [pathname, search, isAuthed]);
}
